import { Gift } from 'lucide-react'
import { type ReactElement, useState } from 'react'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { ManualStart } from '~/shared/ui/manual-start'
import { PromptField } from '~/shared/ui/prompt-field'
import { normalizeUrl } from './map-place'

interface UrlStepProps {
  initialUrl: string
  error: string | null
  onSubmit: (url: string) => void
  manualLink: ReactElement
}

export function UrlStep({ initialUrl, error, onSubmit, manualLink }: UrlStepProps) {
  const [value, setValue] = useState(initialUrl)
  const [invalid, setInvalid] = useState(false)

  const submit = () => {
    const url = normalizeUrl(value)
    if (!url) {
      setInvalid(true)
      return
    }
    onSubmit(url)
  }

  const message = invalid ? m.placeWebInvalid() : error

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-8 pt-6 text-center sm:pt-14">
      <div className="flex flex-col gap-3">
        <h1 className="text-balance font-semibold text-3xl text-foreground leading-[1.1] tracking-[-0.03em] sm:text-4xl">
          {m.placeWebHeadline()}
        </h1>
        <p className="mx-auto max-w-2xl text-balance text-base text-muted-foreground sm:text-lg">
          {m.placeWebSubline()}
        </p>
      </div>

      <PromptField
        value={value}
        onChange={(next) => {
          setValue(next)
          if (invalid) setInvalid(false)
        }}
        onSubmit={submit}
        label={m.placeWebLabel()}
        submitLabel={m.placeWebSubmit()}
        placeholders={[m.placeWebPlaceholderAddress(), m.placeWebPlaceholderPaste()]}
        inputMode="url"
        actions={
          <span className="flex h-9 items-center gap-1.5 rounded-full bg-brand-subtle px-3 font-medium text-[13px] text-brand-subtle-foreground">
            <Gift className="size-4" />
            {m.placeWebFree()}
          </span>
        }
      />

      <p
        role={message ? 'alert' : undefined}
        className={cn('min-h-5 text-sm', message ? 'text-destructive' : 'text-muted-foreground')}
      >
        {message ?? m.placeWebHint()}
      </p>

      <ManualStart>{manualLink}</ManualStart>
    </div>
  )
}
